// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  NetworkVirtualAppliance,
  NetworkVirtualAppliancesListByResourceGroupOptionalParams,
  NetworkVirtualAppliancesListOptionalParams,
  NetworkVirtualAppliancesDeleteOptionalParams,
  NetworkVirtualAppliancesGetOptionalParams,
  NetworkVirtualAppliancesGetResponse,
  TagsObject,
  NetworkVirtualAppliancesUpdateTagsOptionalParams,
  NetworkVirtualAppliancesUpdateTagsResponse,
  NetworkVirtualAppliancesCreateOrUpdateOptionalParams,
  NetworkVirtualAppliancesCreateOrUpdateResponse,
  NetworkVirtualAppliancesRestartOptionalParams,
  NetworkVirtualAppliancesRestartResponse,
  NetworkVirtualAppliancesReimageOptionalParams,
  NetworkVirtualAppliancesReimageResponse,
  NetworkVirtualApplianceBootDiagnosticParameters,
  NetworkVirtualAppliancesGetBootDiagnosticLogsOptionalParams,
  NetworkVirtualAppliancesGetBootDiagnosticLogsResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a NetworkVirtualAppliances. */
export interface NetworkVirtualAppliances {
  /**
   * Lists all Network Virtual Appliances in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  listByResourceGroup(
    resourceGroupName: string,
    options?: NetworkVirtualAppliancesListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<NetworkVirtualAppliance>;
  /**
   * Gets all Network Virtual Appliances in a subscription.
   * @param options The options parameters.
   */
  list(
    options?: NetworkVirtualAppliancesListOptionalParams,
  ): PagedAsyncIterableIterator<NetworkVirtualAppliance>;
  /**
   * Deletes the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Gets the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesGetOptionalParams,
  ): Promise<NetworkVirtualAppliancesGetResponse>;
  /**
   * Updates a Network Virtual Appliance.
   * @param resourceGroupName The resource group name of Network Virtual Appliance.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance being updated.
   * @param parameters Parameters supplied to Update Network Virtual Appliance Tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    parameters: TagsObject,
    options?: NetworkVirtualAppliancesUpdateTagsOptionalParams,
  ): Promise<NetworkVirtualAppliancesUpdateTagsResponse>;
  /**
   * Creates or updates the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param parameters Parameters supplied to the create or update Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    parameters: NetworkVirtualAppliance,
    options?: NetworkVirtualAppliancesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<NetworkVirtualAppliancesCreateOrUpdateResponse>,
      NetworkVirtualAppliancesCreateOrUpdateResponse
    >
  >;
  /**
   * Creates or updates the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param parameters Parameters supplied to the create or update Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    parameters: NetworkVirtualAppliance,
    options?: NetworkVirtualAppliancesCreateOrUpdateOptionalParams,
  ): Promise<NetworkVirtualAppliancesCreateOrUpdateResponse>;
  /**
   * Restarts one or more VMs belonging to the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginRestart(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesRestartOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<NetworkVirtualAppliancesRestartResponse>,
      NetworkVirtualAppliancesRestartResponse
    >
  >;
  /**
   * Restarts one or more VMs belonging to the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginRestartAndWait(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesRestartOptionalParams,
  ): Promise<NetworkVirtualAppliancesRestartResponse>;
  /**
   * Reimages one VM belonging to the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginReimage(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesReimageOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<NetworkVirtualAppliancesReimageResponse>,
      NetworkVirtualAppliancesReimageResponse
    >
  >;
  /**
   * Reimages one VM belonging to the specified Network Virtual Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param options The options parameters.
   */
  beginReimageAndWait(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    options?: NetworkVirtualAppliancesReimageOptionalParams,
  ): Promise<NetworkVirtualAppliancesReimageResponse>;
  /**
   * Retrieves the boot diagnostic logs for a VM instance belonging to the specified Network Virtual
   * Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param request Parameters supplied to retrieve boot diagnostic logs for a NVA VM instance
   * @param options The options parameters.
   */
  beginGetBootDiagnosticLogs(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    request: NetworkVirtualApplianceBootDiagnosticParameters,
    options?: NetworkVirtualAppliancesGetBootDiagnosticLogsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<NetworkVirtualAppliancesGetBootDiagnosticLogsResponse>,
      NetworkVirtualAppliancesGetBootDiagnosticLogsResponse
    >
  >;
  /**
   * Retrieves the boot diagnostic logs for a VM instance belonging to the specified Network Virtual
   * Appliance.
   * @param resourceGroupName The name of the resource group.
   * @param networkVirtualApplianceName The name of Network Virtual Appliance.
   * @param request Parameters supplied to retrieve boot diagnostic logs for a NVA VM instance
   * @param options The options parameters.
   */
  beginGetBootDiagnosticLogsAndWait(
    resourceGroupName: string,
    networkVirtualApplianceName: string,
    request: NetworkVirtualApplianceBootDiagnosticParameters,
    options?: NetworkVirtualAppliancesGetBootDiagnosticLogsOptionalParams,
  ): Promise<NetworkVirtualAppliancesGetBootDiagnosticLogsResponse>;
}
